// src/components/Layout/SmartTable.js
import React from "react";
import { Box, useMediaQuery, useTheme, Typography } from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { alpha } from "@mui/material/styles";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DoneIcon from "@mui/icons-material/Done";
import PendingIcon from "@mui/icons-material/PendingActions";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { PendingActions as TodoIcon } from "@mui/icons-material";
// Role icons for UsersPage mobile
import HandymanIcon from "@mui/icons-material/Handyman";
import CrisisAlertIcon from "@mui/icons-material/CrisisAlert";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import PushPinIcon from "@mui/icons-material/PushPin";
import PeopleIcon from "@mui/icons-material/People";
import {
  Chip,
} from "@mui/material";
import useLandscape from "../../hooks/useLandscape";
import LeadStatusCell from "../LeadStatusCell";
import { LEAD_STATUS_VALUES, normalizeLeadStatusKey } from "../../constants/leadStatuses";


export default function SmartTable({
  rows = [],
  columns = [],
  onRowClick,
  mobileEssentialFields = [],
  landscapeHiddenFields = [],
  pageSize = 50,
}) {
  const apiRef = useGridApiRef();
  const theme = useTheme();

  // These must ALWAYS run
  const isMobile = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(max-width:1024px) and (min-width:769px)");
  const isLandscape = useLandscape();

  // Hooks must ALWAYS run — even if unused on mobile
  const [columnWidths, setColumnWidths] = React.useState({});

  const handleColumnResize = (params) => {
    const containerWidth =
      apiRef.current?.rootElementRef?.current?.clientWidth ?? 0;
    const field = params.colDef.field;

    const clampedWidth = Math.min(
      Math.max(params.width, params.colDef.minWidth || 100),
      params.colDef.maxWidth || containerWidth / 2
    );

    setColumnWidths((prev) => ({ ...prev, [field]: clampedWidth }));
    apiRef.current?.forceUpdate?.();
  };

  const normalizedColumns = React.useMemo(() => {
    return (columns || []).map((col) => ({
      ...col,
      minWidth: col.minWidth ?? 120,
      maxWidth: col.maxWidth ?? 400,
      width: columnWidths[col.field] ?? col.width ?? undefined,
      resizable: true,
      flex: col.flex ?? undefined,
    }));
  }, [columns, columnWidths]);

  // -------------------------------------------------------------------------
  // 🟦 LANDSCAPE MODE — COMPRESSED TABLE VIEW (MANDATORY)
  // -------------------------------------------------------------------------
  if (isLandscape) {
    // Filter columns to show more in landscape (essential + key fields)
    const landscapeColumns = columns.filter(col => {
      // Exclude fields that should be hidden in landscape
      if (landscapeHiddenFields.includes(col.field)) {
        return false;
      }
      
      const essential = mobileEssentialFields || [];
      const landscapeFields = [
        ...essential,
        'email', 'phone', 'status', 'territory_name', 
        'assigned_rep_name', 'city', 'state'
      ];
      return landscapeFields.includes(col.field) || essential.includes(col.field);
    });

    return (
      <Box sx={{ 
        width: "100%", 
        height: "400px", 
        overflowX: "auto", // Horizontal scroll
        overflowY: "auto",
      }}>
        <DataGrid
          apiRef={apiRef}
          getRowId={(row) => row.id ?? row.pk ?? row._id ?? Math.random()}
          rows={Array.isArray(rows) ? rows : []}
          columns={landscapeColumns.map((col) => ({
            ...col,
            renderCell: (params) => {
              if (col.renderCell) return col.renderCell(params);
              if (col.valueGetter) return col.valueGetter(params);
              const value = params.row?.[col.field];
              return value !== undefined && value !== null ? value : "—";
            },
          }))}

          columnResizeMode="onChange"
          columnVisibilityModel={{
            mobile_status: false,
          }}
          experimentalFeatures={{ columnPinning: true }}
          onColumnResize={handleColumnResize}
          disableRowSelectionOnClick
          onRowClick={onRowClick}

          slots={{
            columnUnsortedIcon: UnfoldMoreIcon,
            columnSortedAscendingIcon: ArrowUpwardIcon,
            columnSortedDescendingIcon: ArrowDownwardIcon,
          }}
          sx={{
            border: "none",
            fontSize: "0.7rem", // Shrunk font size
            "& .MuiDataGrid-cell": {
              py: 0.25, // Shrunk row height
              fontSize: "0.7rem",
            },
            "& .MuiDataGrid-columnHeader": {
              py: 0.5, // Shrunk header height
              fontSize: "0.7rem",
              fontWeight: 600,
            },
            "& .MuiDataGrid-row": {
              minHeight: "32px !important", // Shrunk row height
              maxHeight: "32px !important",
            },
            "& .MuiDataGrid-columnHeaders": {
              minHeight: "40px !important",
              maxHeight: "40px !important",
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.05),
              borderBottom: (theme) =>
                `2px solid ${theme.palette.primary.main}`,
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
              fontSize: "0.7rem", // Shrunk font size
              color: (theme) => theme.palette.primary.main,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            },
            "& .MuiDataGrid-cellContent": {
              whiteSpace: "normal",
              wordBreak: "break-word",
              textTransform: "capitalize",
              fontSize: "0.7rem", // Shrunk font size
            },
            "& .MuiDataGrid-row": {
              "&:nth-of-type(odd)": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.background.paper, 0.03)
                    : alpha(theme.palette.primary.main, 0.02),
              },
              "&:hover": {
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.09),
                cursor: "pointer",
              },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
        />
      </Box>
    );
  }

  // -------------------------------------------------------------------------
  // 🟦 MOBILE MODE — RETURN CARD LAYOUT
  // -------------------------------------------------------------------------
  if (isMobile) {
    const essentialColumns =
      mobileEssentialFields.length > 0
        ? columns.filter((col) => mobileEssentialFields.includes(col.field))
        : columns;

    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          mt: 1,
        }}
      >
        {rows.length === 0 && (
          <Typography
            variant="body2"
            sx={{ opacity: 0.7, textAlign: "center", mt: 2 }}
          >
            No records available.
          </Typography>
        )}

        {rows.map((row) => (
          <Box
            key={row.id || Math.random()}
            onClick={() => onRowClick?.({ row })}
            sx={{
              borderRadius: "14px",
              padding: "16px",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.background.paper, 0.6)
                  : "#ffffff",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0px 2px 10px rgba(0,0,0,0.4)"
                  : "0px 2px 10px rgba(0,0,0,0.1)",
              cursor: "pointer",
              transition: "0.2s",
              "&:hover": {
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0px 4px 14px rgba(0,0,0,0.6)"
                    : "0px 4px 16px rgba(0,0,0,0.15)",
              },
            }}
          >
            {/* ---------------------------Title Line---------------------------- */}
            <Typography
  variant="h6"
  sx={{
    fontWeight: 700,
    mb: 1,
    color: theme.palette.primary.main,
    textTransform: "capitalize",
  }}
>
  {
    row.lead_name ||                                     // ⭐ FollowUp fix
    `${row.first_name || ""} ${row.last_name || ""}`.trim() || 
    row.name || 
    (row.lead ? `Lead #${row.lead}` : null) ||           // ⭐ formatted fallback
    row[essentialColumns[0]?.field] || 
    "—"
  }
</Typography>


            {/* ---------------------Essential fields-------------------------- */}
            {essentialColumns.map((col) => {
              const value =
                col.valueGetter?.({ row }) ??
                row[col.field] ??
                "—";

              return (
                <Box
                  key={col.field}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: col.field === "actions" ? "center" : "flex-start",
                    py: 0.5,
                    borderBottom: "1px solid rgba(0,0,0,0.08)",
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, opacity: 0.7 }}
                  >
                    {col.headerName}
                  </Typography>

{/* =============== VALUE CELL (TEXT OR STATUS BADGE)================= === */}
{/* Handle actions field with renderCell */}
{col.field === "actions" && col.renderCell ? (
  <Box sx={{ display: "flex", gap: 1 }}>
    {col.renderCell({ row })}
  </Box>
) : col.field === "status" ? (
  (() => {
    const status = (row.status || "").toLowerCase();

    // TASK STATUS CHIPS
    if (status === "completed") {
      return (
        <Chip
          icon={<DoneIcon sx={{ color: "green !important" }} />}
          label="Completed"
          sx={{
            height: "24px",
            borderRadius: "6px",
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "grey",
            backgroundColor: "rgba(158, 158, 158, 0.12)",
          }}
        />
      );
    }

    if (status === "in progress") {
      return (
        <Chip
          icon={<AutorenewIcon sx={{ color: "#1b53cd !important" }} />}
          label="In Progress"
          sx={{
            height: "24px",
            borderRadius: "6px",
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "#1b53cd",
            backgroundColor: "rgba(158, 158, 158, 0.12)",
          }}
        />
      );
    }

    if (status === "to do" || status === "todo") {
      return (
        <Chip
          icon={<TodoIcon sx={{ color: "#B01E1E !important" }} />}
          label="To Do"
          sx={{
            height: "24px",
            borderRadius: "6px",
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "#b01e1e",
            backgroundColor: "rgba(158, 158, 158, 0.12)",
          }}
        />
      );
    }

    const isLeadStatus = LEAD_STATUS_VALUES.some(
      (v) => normalizeLeadStatusKey(v) === status
    );
    if (isLeadStatus) {
      return (
        <Box sx={{ maxWidth: "55%", display: "flex", justifyContent: "flex-end" }}>
          <LeadStatusCell status={row.status} />
        </Box>
      );
    }

    // Fallback for unknown status
    return (
      <Typography
        variant="body2"
        sx={{
          maxWidth: "55%",
          textAlign: "right",
          wordWrap: "break-word",
          textTransform: "capitalize",
          color: "text.secondary",
          fontStyle: "italic",
        }}
      >
        {row.status || "—"}
      </Typography>
    );
  })()
) : col.field === "priority" ? (
  (() => {
    const priority = (row.priority || "").toLowerCase();
    let color = "text.primary";
    
    if (priority === "low") {
      color = "gray";
    } else if (priority === "medium") {
      color = "#FB8C00"; // orange
    } else if (priority === "high") {
      color = "#E65100"; // red
    }
    
    return (
      <Typography
        variant="body2"
        sx={{
          maxWidth: "55%",
          textAlign: "right",
          wordWrap: "break-word",
          color: color,
          fontWeight: 500,
        }}
      >
        {row.priority ? row.priority.charAt(0).toUpperCase() + row.priority.slice(1).toLowerCase() : "—"}
      </Typography>
    );
  })()
) : col.field === "mobile_status" ? (
  (() => {
    const status = row.mobile_status || "";

    // FOLLOW-UPS STATUS SET
    if (["Completed", "Overdue", "In Progress"].includes(status)) {
      const isDone = status === "Completed";
      const isOverdue = status === "Overdue";

      return (
        <Chip
          label={status}
          icon={
            isDone ? (
              <DoneIcon sx={{ color: "green !important" }} />
            ) : isOverdue ? (
              <PendingIcon sx={{ color: "#B01E1E !important" }} />
            ) : (
              <AutorenewIcon sx={{ color: "#1b53cd !important" }} />
            )
          }
          sx={{
            height: "24px",
            borderRadius: "6px",
            fontSize: "0.75rem",
            fontWeight: 600,
            color: isDone
              ? "green"
              : isOverdue
              ? "#B01E1E"
              : "#1b53cd",
            backgroundColor: isDone
              ? "rgba(46, 125, 50, 0.15)"
              : isOverdue
              ? "rgba(244, 67, 54, 0.15)"
              : "rgba(158, 158, 158, 0.1)",
          }}
        />
      );
    }

    // NOTIFICATION STATUS SET
    if (["Read", "New"].includes(status)) {
      const isRead = status === "Read";
      return (
        <Chip
          label={status}
          icon={
            isRead ? (
              <DoneIcon sx={{ color: "green !important" }} />
            ) : (
              <NotificationsActiveIcon sx={{ color: "#bd251a !important" }} />
            )
          }
          sx={{
            height: "24px",
            borderRadius: "6px",
            fontSize: "0.75rem",
            fontWeight: 600,
            color: isRead ? "green" : "#bd251a",
            backgroundColor: isRead
              ? "rgba(158, 158, 158, 0.12)"
              : "rgba(158, 35, 35, 0.1)",
          }}
        />
      );
    }

    // FALLBACK FOR UNKNOWN STATUS TEXT
    return (
      <Typography
        variant="body2"
        sx={{
          maxWidth: "55%",
          textAlign: "right",
          wordWrap: "break-word",
          textTransform: "capitalize",
        }}
      >
        {status || "—"}
      </Typography>
    );
  })()
) : col.field === "email" ? (
  <Typography
    variant="body2"
    sx={{
      maxWidth: "65%",
      textAlign: "right",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontSize: "0.8rem",
    }}
    title={value}
  >
    {value || "—"}
  </Typography>
) : (
  <Typography
    variant="body2"
    sx={{
      maxWidth: "55%",
      textAlign: "right",
      wordWrap: "break-word",
      textTransform: "capitalize",
    }}
  >
    {value || "—"}
  </Typography>
)}

                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    );
  }

  // -------------------------------------------------------------------------
  // 🟦 DESKTOP + TABLET MODE — RETURNS DATAGRID
  // -------------------------------------------------------------------------
  return (
    <Box
      sx={{
        width: "100%",
        
        maxWidth: "100%",
        overflowX: isTablet ? "auto" : "visible",
        pb: isTablet ? 1 : 0,
      }}
    >
      <DataGrid
        apiRef={apiRef}
        getRowId={(row) => row.id ?? row.pk ?? row._id ?? Math.random()}
        rows={Array.isArray(rows) ? rows : []}
        columns={normalizedColumns.map((col) => ({
          ...col,
          renderCell: (params) => {
            if (col.renderCell) return col.renderCell(params);
            if (col.valueGetter) return col.valueGetter(params);
            const value = params.row?.[col.field];
            return value !== undefined && value !== null ? value : "—";
          },
        }))}

          pageSizeOptions={[10, 25, 50, 100]}
  initialState={{
    pagination: { paginationModel: { pageSize } }
  }}

        autoHeight
        columnResizeMode="onChange"
        columnVisibilityModel={{
  mobile_status: false,
}}

        experimentalFeatures={{ columnPinning: true }}
        onColumnResize={handleColumnResize}
        disableRowSelectionOnClick
        onRowClick={onRowClick}
        slots={{
          columnUnsortedIcon: UnfoldMoreIcon,
          columnSortedAscendingIcon: ArrowUpwardIcon,
          columnSortedDescendingIcon: ArrowDownwardIcon,
        }}
        sx={{
          border: "none",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: (theme) =>
              alpha(theme.palette.primary.main, 0.05),
            borderBottom: (theme) =>
              `2px solid ${theme.palette.primary.main}`,
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
            fontSize: "0.8rem",
            color: (theme) => theme.palette.primary.main,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          },
          "& .MuiDataGrid-cellContent": {
            whiteSpace: "normal",
            wordBreak: "break-word",
            textTransform: "capitalize",
          },
          "& .MuiDataGrid-row": {
            "&:nth-of-type(odd)": {
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.background.paper, 0.03)
                  : alpha(theme.palette.primary.main, 0.02),
            },
            "&:hover": {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.09),
              cursor: "pointer",
            },
          },
        }}
      />
    </Box>
  );
}
